import { LitElement, html } from 'lit';
import { customElement, state, property } from 'lit/decorators.js';
import { styles } from './safety-action-modal.style.js';

import { SafetyActionStatus } from '@cmms/models';
import { getUsersByCompany, getSafetyReportsByCompany, createAction, updateAction } from '@cmms/api';
import { db } from '../../services/auth.service.js';

@customElement('safety-action-modal')
export class SafetyActionModal extends LitElement {
  @property({ type: Boolean }) open = false;
  @property({ type: Object }) action: any = null;
  @property({ type: String }) companyId: string = '';

  @state() private users: any[] = [];
  @state() private safetyReports: any[] = [];
  @state() private isLoadingData = false;

  static styles = styles;

  @state() private formData: any = {
    action_title: '',
    action_description: '',
    status: SafetyActionStatus.TODO,
    due_date: '',
    assigned_to_user_id: '',
    related_report_id: '',
    completion_notes: ''
  };
  @state() private loading = false;
  @state() private error = '';

  connectedCallback() {
    super.connectedCallback();
    this.loadUsersAndReports();
  }

  updated(changedProperties: Map<string, unknown>) {
    super.updated(changedProperties);
    if (changedProperties.has('action') && this.action) {
      this.formData = { 
        ...this.action,
        due_date: this.action.due_date ? new Date(this.action.due_date).toISOString().split('T')[0] : ''
      };
    }
    if (changedProperties.has('companyId') && this.companyId) {
      this.loadUsersAndReports();
    }
  }

  private async loadUsersAndReports() {
    if (!this.companyId) return;
    
    try {
      this.isLoadingData = true;
      
      const [usersResult, reportsResult] = await Promise.all([
        getUsersByCompany(db, this.companyId),
        getSafetyReportsByCompany(db, this.companyId)
      ]);


      if (usersResult.data) {
        // Extract the actual data from the model property
        this.users = usersResult.data.map(item => ({
          id: item.id,
          ...(item.model as any).toJSON()
        }));
      }
      if (reportsResult.data) {
        // Extract the actual data from the model property
        this.safetyReports = reportsResult.data.map(item => ({
          id: item.id,
          ...(item.model as any).toJSON()
        }));
      }
    } catch (error) {
      // Handle error silently or show user-friendly message
    } finally {
      this.isLoadingData = false;
    }
  }

  private async saveAction() {
    if (!this.validateForm()) {
      return;
    }

    this.loading = true;
    this.error = '';

    try {
      const response = this.action 
        ? await updateAction(db, this.action.id, this.formData)
        : await createAction(db, this.formData);
      
      if (response.error) {
        throw new Error(response.error);
      }
      
      this.dispatchEvent(new CustomEvent('safety-action-saved', {
        detail: { action: this.formData },
        bubbles: true
      }));
      
      this.closeModal();
    } catch (err) {
      this.error = 'Failed to save safety action';
      console.error('Error saving safety action:', err);
    } finally {
      this.loading = false;
    }
  }

  private validateForm() {
    if (!this.formData.action_title.trim()) {
      this.error = 'Action title is required';
      return false;
    }
    if (!this.formData.action_description.trim()) {
      this.error = 'Action description is required';
      return false;
    }
    if (!this.formData.due_date) {
      this.error = 'Due date is required';
      return false;
    }
    if (!this.formData.assigned_to_user_id.trim()) {
      this.error = 'Assigned user is required';
      return false;
    }
    return true;
  }

  private closeModal() {
    this.dispatchEvent(new CustomEvent('modal-closed', { bubbles: true, composed: true }));
    this.open = false;
  }

  private onInputChange(field: string, value: any) {
    this.formData = { ...this.formData, [field]: value };
  }

  render() {
    if (!this.open) return html``;

    return html`
      <sl-dialog
        label="${this.action ? 'Edit Safety Action' : 'New Safety Action'}"
        .open=${this.open}
        @sl-hide=${this.closeModal}
      >
        ${this.loading ? html`
          <div class="loading-overlay">
            <sl-spinner></sl-spinner>
          </div>
        ` : ''}
        ${this.isLoadingData ? html`
          <div class="loading-overlay">
            <sl-spinner></sl-spinner>
            <p>Loading data...</p>
          </div>
        ` : ''}

        <form @submit=${(e: Event) => { e.preventDefault(); this.saveAction(); }} 
          @click=${(e: Event) => e.stopPropagation()} 
          @sl-input=${(e: Event) => e.stopPropagation()} 
          @sl-change=${(e: Event) => e.stopPropagation()}
          @sl-open-change=${(e: Event) => e.stopPropagation()}
          @sl-show=${(e: Event) => e.stopPropagation()}
          @sl-hide=${(e: Event) => e.stopPropagation()}
        >
          <div class="form-container">
            ${this.error ? html`
              <sl-alert variant="danger" open>
                <sl-icon name="exclamation-triangle" slot="icon"></sl-icon>
                ${this.error}
              </sl-alert>
            ` : ''}

            <div class="form-group">
              <label class="form-label">
                Action Title <span class="required">*</span>
              </label>
              <sl-input
                value="${this.formData.action_title}"
                placeholder="Enter action title"
                @input="${(e: any) => this.onInputChange('action_title', e.target.value)}"
                @click=${(e: Event) => e.stopPropagation()}
              ></sl-input>
            </div>

          <div class="form-group">
            <label class="form-label">
              Action Description <span class="required">*</span>
            </label>
            <sl-textarea
              value="${this.formData.action_description}"
              placeholder="Describe the action to be taken"
              rows="4"
              @input="${(e: any) => this.onInputChange('action_description', e.target.value)}"
              @click=${(e: Event) => e.stopPropagation()}
            ></sl-textarea>
          </div>

          <div class="form-row">
            <div class="form-group">
              <label class="form-label">
                Status <span class="required">*</span>
              </label>
              <sl-select
                value="${this.formData.status}"
                @sl-change="${(e: any) => this.onInputChange('status', e.target.value)}"
                @sl-open-change=${(e: Event) => e.stopPropagation()}
                @sl-show=${(e: Event) => e.stopPropagation()}
                @sl-hide=${(e: Event) => e.stopPropagation()}
                @click=${(e: Event) => e.stopPropagation()}
              >
                <sl-option value="${SafetyActionStatus.TODO}">To Do</sl-option>
                <sl-option value="${SafetyActionStatus.IN_PROGRESS}">In Progress</sl-option>
                <sl-option value="${SafetyActionStatus.COMPLETED}">Completed</sl-option>
              </sl-select>
            </div>

            <div class="form-group">
              <label class="form-label">
                Due Date <span class="required">*</span>
              </label>
              <sl-input
                type="date"
                value="${this.formData.due_date}"
                @input="${(e: any) => this.onInputChange('due_date', e.target.value)}"
                @click=${(e: Event) => e.stopPropagation()}
              ></sl-input>
            </div>
          </div>

          <div class="form-row">
            <div class="form-group">
              <label class="form-label">
                Assigned To User <span class="required">*</span>
              </label>
              <sl-select
                value="${this.formData.assigned_to_user_id || ''}"
                placeholder="Select user"
                @sl-change="${(e: any) => this.onInputChange('assigned_to_user_id', e.target.value)}"
                @sl-open-change=${(e: Event) => e.stopPropagation()}
                @sl-show=${(e: Event) => e.stopPropagation()}
                @sl-hide=${(e: Event) => e.stopPropagation()}
                @click=${(e: Event) => e.stopPropagation()}
              >
                <sl-option value="">Select a user</sl-option>
                ${this.users.map(user => html`
                  <sl-option value="${user.id}">${user.name || user.email || user.id}</sl-option>
                `)}
              </sl-select>
            </div>

            <div class="form-group">
              <label class="form-label">Related Report</label>
              <sl-select
                value="${this.formData.related_report_id || ''}"
                placeholder="Select report (optional)"
                @sl-change="${(e: any) => this.onInputChange('related_report_id', e.target.value)}"
                @sl-open-change=${(e: Event) => e.stopPropagation()}
                @sl-show=${(e: Event) => e.stopPropagation()}
                @sl-hide=${(e: Event) => e.stopPropagation()}
                @click=${(e: Event) => e.stopPropagation()}
              >
                <sl-option value="">Select a report (optional)</sl-option>
                ${this.safetyReports.map(report => {
                  const displayTitle = report.title || report.report_title || report.incident_title || report.id;
                  return html`
                    <sl-option value="${report.id}">${displayTitle}</sl-option>
                  `;
                })}
              </sl-select>
            </div>
          </div>

          ${this.formData.status === SafetyActionStatus.COMPLETED ? html`
            <div class="form-group">
              <label class="form-label">Completion Notes</label>
              <sl-textarea
                value="${this.formData.completion_notes || ''}"
                placeholder="Enter completion notes"
                rows="3"
                @input="${(e: any) => this.onInputChange('completion_notes', e.target.value)}"
                @click=${(e: Event) => e.stopPropagation()}
              ></sl-textarea>
            </div>
          ` : ''}
          </div>

          <div class="form-actions">
            <sl-button @click="${this.closeModal}">Cancel</sl-button>
            <sl-button 
              variant="primary" 
              type="submit"
              ?loading="${this.loading}"
            >
              ${this.action ? 'Update Action' : 'Create Action'}
            </sl-button>
          </div>
        </form>
      </sl-dialog>
    `;
  }
}
